import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// IMPORTS custom
import NationalitiesSelect from '../NationalitiesSelect';
import NationalitiesSelect2 from '../NationalitiesSelect2';
import SelectSelectionNational from '../SelectSelectionNational';

// Fonctions de formattage de date
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Fonction utilitaire pour tronquer un texte trop long (utilisé uniquement en mode modification)
const truncateText = (text, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

function InformacionBasica({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  allGroups,
  handleChange,
  handleGroupChange
}) {
  const { t } = useTranslation();

  // Composant pour le champ "Origen Genético" en mode modification
  const OrigenGeneticoSelect = () => (
    <FormControl fullWidth>
      <InputLabel>{t('createPlayer.fields.geneticOrigin')}</InputLabel>
      <Select
        name="origen_genetico"
        value={editablePlayer.origen_genetico || ''}
        onChange={handleChange}
        label={t('createPlayer.fields.geneticOrigin')}
        renderValue={(selected) => {
          // Map pour afficher les traductions dans le select
          const displayValue = t(`createPlayer.geneticOrigins.${selected}`, selected);
          return (
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
            >
              {truncateText(displayValue, 30)}
            </Box>
          );
        }}
        sx={{
          '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }
        }}
        endAdornment={
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setEditablePlayer((prev) => ({
                ...prev,
                origen_genetico: ''
              }));
            }}
            sx={{
              visibility: editablePlayer.origen_genetico ? 'visible' : 'hidden',
              position: 'absolute',
              right: 32
            }}
            size="small"
          >
            <Clear fontSize="small" />
          </IconButton>
        }
      >
        <MenuItem
          value="subsaharianAfrican"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.subsaharianAfrican')}
        </MenuItem>
        <MenuItem
          value="afroCaribbean"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.afroCaribbean')}
        </MenuItem>
        <MenuItem
          value="balkans"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.balkans')}
        </MenuItem>
        <MenuItem
          value="westernEurope"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.westernEurope')}
        </MenuItem>
        <MenuItem
          value="latinAmerican"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.latinAmerican')}
        </MenuItem>
        <MenuItem
          value="northAfrican"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.northAfrican')}
        </MenuItem>
        <MenuItem
          value="northAmerican"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {t('createPlayer.geneticOrigins.northAmerican')}
        </MenuItem>
      </Select>
    </FormControl>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper',
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          }}
        >
          1. {t('createPlayer.sections.basicInfo')}
        </Typography>

        <Box sx={{ p: 2 }}>
          {isEditing ? (
            <Grid
              container
              spacing={2}
              sx={{
                maxWidth: '100%',
                '& .MuiInputBase-input': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }
              }}
            >
              {/* Nom */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.name')}
                  name="nom"
                  value={editablePlayer.nom || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* Groupe (Type) */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.group')}</InputLabel>
                  <Select
                    name="type"
                    value={editablePlayer.type || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.group')}
                    endAdornment={
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditablePlayer((prev) => ({ ...prev, type: '' }));
                        }}
                        sx={{
                          visibility: editablePlayer.type ? 'visible' : 'hidden',
                          position: 'absolute',
                          right: 32
                        }}
                        size="small"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    }
                  >
                    <MenuItem value="pro">{t('createPlayer.fields.pro')}</MenuItem>
                    <MenuItem value="cantera">{t('createPlayer.fields.cantera')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Groupes */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.groups')}</InputLabel>
                  <Select
                    multiple
                    value={editablePlayer.groupes || []}
                    renderValue={(selected) =>
                      selected.map((g) => g.libelle).join(', ')
                    }
                    endAdornment={
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditablePlayer((prev) => ({
                            ...prev,
                            groupes: []
                          }));
                        }}
                        sx={{
                          visibility: editablePlayer.groupes?.length
                            ? 'visible'
                            : 'hidden',
                          position: 'absolute',
                          right: 32
                        }}
                        size="small"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: '90vw',
                          maxWidth: '90vw'
                        }
                      },
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' }
                    }}
                  >
                    {allGroups.map((group) => (
                      <MenuItem
                        key={group.id}
                        value={group}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          py: 1
                        }}
                      >
                        <Checkbox
                          checked={
                            editablePlayer.groupes?.some(
                              (g) => g.id === group.id
                            ) || false
                          }
                          onChange={(e) => handleGroupChange(e, group)}
                          style={{ color: group.code_couleur || '#000' }}
                        />
                        <ListItemText primary={group.libelle} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Poids */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.weight')}
                  name="peso"
                  type="number"
                  value={editablePlayer.peso || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* Taille */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.height')}
                  name="altura"
                  type="number"
                  value={editablePlayer.altura || editablePlayer.taille || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* Nacionalité */}
              <Grid item xs={12}>
                <NationalitiesSelect
                  value={editablePlayer.nacionalidad || ''}
                  onChange={(e) =>
                    setEditablePlayer((prev) => ({
                      ...prev,
                      nacionalidad: e.target.value
                    }))
                  }
                />
              </Grid>

              {/* 2ème Nacionalité */}
              <Grid item xs={12}>
                <NationalitiesSelect2
                  value={editablePlayer.segundaNacionalidad || ''}
                  onChange={(e) =>
                    setEditablePlayer((prev) => ({
                      ...prev,
                      segundaNacionalidad: e.target.value
                    }))
                  }
                />
              </Grid>

              {/* Origine génétique en modification */}
              <Grid item xs={12}>
                <OrigenGeneticoSelect />
              </Grid>

              {/* Date de naissance */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.birthDate')}
                  name="dateNaissance"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formatDateForInput(editablePlayer.dateNaissance)}
                  onChange={(e) =>
                    setEditablePlayer((prev) => ({
                      ...prev,
                      dateNaissance: e.target.value
                    }))
                  }
                />
              </Grid>

              {/* Licence */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.license')}</InputLabel>
                  <Select
                    name="pasaporte"
                    value={editablePlayer.pasaporte || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.license')}
                    endAdornment={
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditablePlayer((prev) => ({
                            ...prev,
                            pasaporte: ''
                          }));
                        }}
                        sx={{
                          visibility: editablePlayer.pasaporte ? 'visible' : 'hidden',
                          position: 'absolute',
                          right: 32
                        }}
                        size="small"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    }
                  >
                    <MenuItem value="EXT">EXT</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="COT">COT</MenuItem>
                    <MenuItem value="JFL">JFL</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          ) : (
            // Mode lecture mobile (affichage complet, sans troncature)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!!player.nom && (
                <Typography>
                  <strong>{t('createPlayer.fields.name')}:</strong> {player.nom}
                </Typography>
              )}
              {!!player.type && (
                <Typography>
                  <strong>{t('createPlayer.fields.group')} :</strong>{' '}
                  {(() => {
                    const typeMap = {
                      'pro': t('createPlayer.fields.pro'),
                      'cantera': t('createPlayer.fields.cantera')
                    };
                    return typeMap[player.type] || player.type.charAt(0).toUpperCase() + player.type.slice(1);
                  })()}
                </Typography>
              )}
              {!!player.groupes?.length && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {player.groupes.map((g) => (
                    <Box
                      key={g.id}
                      sx={{
                        backgroundColor: g.code_couleur || 'grey',
                        color: '#fff',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: 14
                      }}
                    >
                      {g.libelle}
                    </Box>
                  ))}
                </Box>
              )}
              {!!player.peso && (
                <Typography>
                  <strong>{t('createPlayer.fields.weight')}:</strong> {player.peso}kg
                </Typography>
              )}
              {!!player.taille && (
                <Typography>
                  <strong>{t('createPlayer.fields.height')}:</strong> {player.taille} cm
                </Typography>
              )}
              {(!!player.nacionalidad || !!player.segundaNacionalidad) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                  {!!player.nacionalidad && (
                    <Typography sx={{ display: 'flex', flex: 1 }}>
                      <strong>{t('createPlayer.fields.nationality')}:&nbsp;</strong> {t(`createPlayer.nationalities.${player.nacionalidad}`, player.nacionalidad)}
                    </Typography>
                  )}
                  {!!player.segundaNacionalidad && (
                    <Typography sx={{ display: 'flex', flex: 1 }}>
                      <strong>{t('createPlayer.fields.secondNationality')}:&nbsp;</strong> {t(`createPlayer.nationalities.${player.segundaNacionalidad}`, player.segundaNacionalidad)}
                    </Typography>
                  )}
                </Box>
              )}
              {!!player.origen_genetico && (() => {
                // Map pour les nouvelles clés (format actuel)
                const geneticOriginMap = {
                  'subsaharianAfrican': t('createPlayer.geneticOrigins.subsaharianAfrican'),
                  'afroCaribbean': t('createPlayer.geneticOrigins.afroCaribbean'),
                  'balkans': t('createPlayer.geneticOrigins.balkans'),
                  'westernEurope': t('createPlayer.geneticOrigins.westernEurope'),
                  'latinAmerican': t('createPlayer.geneticOrigins.latinAmerican'),
                  'northAfrican': t('createPlayer.geneticOrigins.northAfrican'),
                  'northAmerican': t('createPlayer.geneticOrigins.northAmerican')
                };

                // Map pour les anciennes valeurs complètes (rétrocompatibilité)
                const oldFormatMap = {
                  'Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.': t('createPlayer.geneticOrigins.subsaharianAfrican'),
                  'Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.': t('createPlayer.geneticOrigins.afroCaribbean'),
                  'Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.': t('createPlayer.geneticOrigins.balkans'),
                  'Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.': t('createPlayer.geneticOrigins.westernEurope'),
                  'Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.': t('createPlayer.geneticOrigins.latinAmerican'),
                  'Nordafricanos - Tunez, Marruecos, Egipto, etc.': t('createPlayer.geneticOrigins.northAfrican'),
                  'Norteamericanos - Estados Unidos, Canada, etc.': t('createPlayer.geneticOrigins.northAmerican')
                };

                const translatedValue = geneticOriginMap[player.origen_genetico] || oldFormatMap[player.origen_genetico] || player.origen_genetico;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.geneticOrigin')}:</strong> {translatedValue}
                  </Typography>
                );
              })()}
              {!!player.dateNaissance && (
                <Typography>
                  <strong>{t('createPlayer.fields.birthDate')}:</strong> {formatDateForDisplay(player.dateNaissance)}
                </Typography>
              )}
              {!!player.pasaporte && (
                <Typography>
                  <strong>{t('createPlayer.fields.license')}:</strong> {player.pasaporte}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Version Desktop
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        1. {t('createPlayer.sections.basicInfo')}
      </Typography>
      {isEditing ? (
        <Grid container spacing={2}>
          {/* Nom */}
          <Grid item xs={12} md={6}>
            <TextField
              label={t('createPlayer.fields.name')}
              name="nom"
              value={editablePlayer.nom || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Groupe (Type) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.group')}</InputLabel>
              <Select
                name="type"
                value={editablePlayer.type || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.group')}
                endAdornment={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditablePlayer((prev) => ({ ...prev, type: '' }));
                    }}
                    sx={{
                      visibility: editablePlayer.type ? 'visible' : 'hidden',
                      position: 'absolute',
                      right: 32
                    }}
                    size="small"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                }
              >
                <MenuItem value="pro">Pro</MenuItem>
                <MenuItem value="cantera">{t('createPlayer.fields.cantera')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Groupes */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.groups')}</InputLabel>
              <Select
                multiple
                value={editablePlayer.groupes || []}
                renderValue={(selected) =>
                  selected.map((g) => g.libelle).join(', ')
                }
                endAdornment={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditablePlayer((prev) => ({
                        ...prev,
                        groupes: []
                      }));
                    }}
                    sx={{
                      visibility: editablePlayer.groupes?.length
                        ? 'visible'
                        : 'hidden',
                      position: 'absolute',
                      right: 32
                    }}
                    size="small"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                }
              >
                {allGroups.map((group) => (
                  <MenuItem key={group.id} value={group}>
                    <Checkbox
                      checked={
                        editablePlayer.groupes?.some((g) => g.id === group.id)
                          ? true
                          : false
                      }
                      onChange={(e) => handleGroupChange(e, group)}
                      style={{ color: group.code_couleur || '#000' }}
                    />
                    <ListItemText primary={group.libelle} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Poids */}
          <Grid item xs={12} md={6}>
            <TextField
              label={t('createPlayer.fields.weight')}
              name="peso"
              type="number"
              value={editablePlayer.peso || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Taille */}
          <Grid item xs={12} md={6}>
            <TextField
              label={t('createPlayer.fields.height')}
              name="altura"
              type="number"
              value={editablePlayer.altura || editablePlayer.taille || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          {/* Nacionalité */}
          <Grid item xs={12} md={6}>
            <NationalitiesSelect
              value={editablePlayer.nacionalidad || ''}
              onChange={(e) =>
                setEditablePlayer((prev) => ({
                  ...prev,
                  nacionalidad: e.target.value
                }))
              }
            />
          </Grid>

          {/* 2ème Nacionalité - même ligne que Nacionalidad, colonne droite sous altura */}
          <Grid item xs={12} md={6}>
            <NationalitiesSelect2
              value={editablePlayer.segundaNacionalidad || ''}
              onChange={(e) =>
                setEditablePlayer((prev) => ({
                  ...prev,
                  segundaNacionalidad: e.target.value
                }))
              }
            />
          </Grid>

          {/* Origine génétique en modification */}
          <Grid item xs={12} md={6}>
            <OrigenGeneticoSelect />
          </Grid>

          {/* Date de naissance */}
          <Grid item xs={12} md={4}>
            <TextField
              label={t('createPlayer.fields.birthDate')}
              name="dateNaissance"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formatDateForInput(editablePlayer.dateNaissance)}
              onChange={(e) =>
                setEditablePlayer((prev) => ({
                  ...prev,
                  dateNaissance: e.target.value
                }))
              }
            />
          </Grid>

          {/* Licence */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.license')}</InputLabel>
              <Select
                name="pasaporte"
                value={editablePlayer.pasaporte || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.license')}
                endAdornment={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditablePlayer((prev) => ({
                        ...prev,
                        pasaporte: ''
                      }));
                    }}
                    sx={{
                      visibility: editablePlayer.pasaporte ? 'visible' : 'hidden',
                      position: 'absolute',
                      right: 32
                    }}
                    size="small"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                }
              >
                <MenuItem value="EXT">EXT</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="COT">COT</MenuItem>
                <MenuItem value="JFL">JFL</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        // Mode lecture Desktop (affichage complet, sans troncature)
        <Box sx={{ mt: 0.5 }}>
          <Grid container spacing={0.5}>
            {!!player.nom && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.name')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {player.nom}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.type && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.group')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {(() => {
                      const typeMap = {
                        'pro': t('createPlayer.fields.pro'),
                        'cantera': t('createPlayer.fields.cantera')
                      };
                      return typeMap[player.type] || player.type.charAt(0).toUpperCase() + player.type.slice(1);
                    })()}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.groupes?.length && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.groups')}:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5
                    }}
                  >
                    {player.groupes.map((g) => (
                      <Box
                        key={g.id}
                        sx={{
                          backgroundColor: g.code_couleur || 'grey',
                          color: '#fff',
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          fontSize: '0.825rem',
                          fontWeight: 500
                        }}
                      >
                        {g.libelle}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
            {!!player.peso && (
              <Grid item xs={6} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.weight')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {player.peso} kg
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.taille && (
              <Grid item xs={6} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.height')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {player.taille} cm
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.nacionalidad && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.nationality')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {t(`createPlayer.nationalities.${player.nacionalidad}`, player.nacionalidad)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.segundaNacionalidad && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.secondNationality')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {t(`createPlayer.nationalities.${player.segundaNacionalidad}`, player.segundaNacionalidad)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.origen_genetico && (() => {
              // Map pour les nouvelles clés (format actuel)
              const geneticOriginMap = {
                'subsaharianAfrican': t('createPlayer.geneticOrigins.subsaharianAfrican'),
                'afroCaribbean': t('createPlayer.geneticOrigins.afroCaribbean'),
                'balkans': t('createPlayer.geneticOrigins.balkans'),
                'westernEurope': t('createPlayer.geneticOrigins.westernEurope'),
                'latinAmerican': t('createPlayer.geneticOrigins.latinAmerican'),
                'northAfrican': t('createPlayer.geneticOrigins.northAfrican'),
                'northAmerican': t('createPlayer.geneticOrigins.northAmerican')
              };

              // Map pour les anciennes valeurs complètes (rétrocompatibilité)
              const oldFormatMap = {
                'Africanos (subsahariana) - Camerun, Nigeria, Senegal, Mali, etc.': t('createPlayer.geneticOrigins.subsaharianAfrican'),
                'Afrocaribenos - Martinica, Republica Dominicana, Puerto Rico, Islas Virgenes, etc.': t('createPlayer.geneticOrigins.afroCaribbean'),
                'Balcanes - Serbia, Croacia, Bosnia, Montenegro, Macedonia del Norte, etc.': t('createPlayer.geneticOrigins.balkans'),
                'Europa Occidental - Francia, Alemania, Espana, Italia, Belgica, etc.': t('createPlayer.geneticOrigins.westernEurope'),
                'Latinoamericanos - Argentina, Brasil, Venezuela, Mexico, etc.': t('createPlayer.geneticOrigins.latinAmerican'),
                'Nordafricanos - Tunez, Marruecos, Egipto, etc.': t('createPlayer.geneticOrigins.northAfrican'),
                'Norteamericanos - Estados Unidos, Canada, etc.': t('createPlayer.geneticOrigins.northAmerican')
              };

              const translatedValue = geneticOriginMap[player.origen_genetico] || oldFormatMap[player.origen_genetico] || player.origen_genetico;
              return (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em'
                      }}
                    >
                      {t('createPlayer.fields.geneticOrigin')}:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: 'text.primary',
                        fontWeight: 400
                      }}
                    >
                      {translatedValue}
                    </Typography>
                  </Box>
                </Grid>
              );
            })()}
            {!!player.dateNaissance && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.birthDate')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {formatDateForDisplay(player.dateNaissance)}
                  </Typography>
                </Box>
              </Grid>
            )}
            {!!player.pasaporte && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.license')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {player.pasaporte}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default InformacionBasica;
